/**
 * Shows what the engineer changed during draft review, by comparing the task's current
 * request and rubric with the snapshot taken when the expert submitted the draft.
 * Used on the expert's task page (execution stage and after a send-back), in the draft
 * editor, and on the engineer's page.
 */
import { useState } from 'react'
import { RubricView } from './RubricView'
import { wordDiff, hasChanges } from '@/lib/diff'
import type { DraftSnapshot, RubricCriterion, Task } from '@/lib/types'

export interface RubricChange {
  kind: 'added' | 'removed' | 'changed'
  before?: RubricCriterion
  after?: RubricCriterion
}

export interface ReviewDiff {
  promptChanged: boolean
  rubric: RubricChange[]
  layoutChanged: boolean
  any: boolean
}

const critKey = (c: RubricCriterion) => JSON.stringify([c.criterion, c.role, c.evidence ?? '', c.rationale ?? '', c.decision_anchor ?? '', c.source ?? '', c.visibility ?? '', c.judgment ?? ''])

/** Compute the review changes for a task, or null when there is no snapshot to compare with. */
export function reviewDiff(task: Task): ReviewDiff | null {
  const snap: DraftSnapshot | null | undefined = task.draft_snapshot
  if (!snap) return null
  const promptChanged = hasChanges(wordDiff(snap.prompt ?? '', task.prompt ?? ''))
  const before = new Map((snap.rubric ?? []).map((c) => [c.criterion_id, c]))
  const after = new Map((task.rubric ?? []).map((c) => [c.criterion_id, c]))
  const rubric: RubricChange[] = []
  for (const [id, b] of before) {
    const a = after.get(id)
    if (!a) rubric.push({ kind: 'removed', before: b })
    else if (critKey(a) !== critKey(b)) rubric.push({ kind: 'changed', before: b, after: a })
  }
  for (const [id, a] of after) if (!before.has(id)) rubric.push({ kind: 'added', after: a })
  const layoutChanged = JSON.stringify(task.rubric_layout ?? []) !== JSON.stringify(snap.rubric_layout ?? [])
  return { promptChanged, rubric, layoutChanged, any: promptChanged || rubric.length > 0 || layoutChanged }
}

/** The request text with the review's edits marked inline. Falls back to plain text when unchanged. */
export function PromptWithChanges({ task, label = 'Changes since draft submission' }: { task: Task; label?: string }) {
  const snap = task.draft_snapshot
  const [showOriginal, setShowOriginal] = useState(false)
  if (!snap || !hasChanges(wordDiff(snap.prompt, task.prompt))) return <pre>{task.prompt}</pre>
  const ops = wordDiff(snap.prompt, task.prompt)
  return (
    <div className="changed-block">
      <div className="changed-head">
        <span className="chip warn">{label}</span>
        <button type="button" className="link small" onClick={() => setShowOriginal(!showOriginal)}>{showOriginal ? 'Show marked changes' : 'Show what you submitted'}</button>
      </div>
      {showOriginal
        ? <pre>{snap.prompt}</pre>
        : <pre className="diff">{ops.map((o, i) => o.kind === 'same' ? <span key={i}>{o.text}</span> : o.kind === 'add' ? <ins key={i}>{o.text}</ins> : <del key={i}>{o.text}</del>)}</pre>}
    </div>
  )
}

/** Summary banner listing every change the review made. Renders nothing when there is none. */
export function ReviewChangesSummary({ task }: { task: Task; who?: string }) {
  const d = reviewDiff(task)
  if (!d?.any) return null
  const counts = { added: 0, removed: 0, changed: 0 }
  for (const c of d.rubric) counts[c.kind]++
  const parts: string[] = []
  if (d.promptChanged) parts.push('agent prompt changed')
  if (d.layoutChanged) parts.push('rubric organisation changed')
  if (counts.added) parts.push(`${counts.added} rubric criteri${counts.added === 1 ? 'on' : 'a'} added`)
  if (counts.changed) parts.push(`${counts.changed} rubric criteria changed`)
  if (counts.removed) parts.push(`${counts.removed} rubric criteria removed`)
  return (
    <div className="note warn changes-note">
      <div className="who">Changes since draft submission</div>
      {parts.join(', ')} compared with the draft you submitted. The changes are marked below.
    </div>
  )
}

/** Rubric with review changes marked per criterion (added, changed with the previous text, removed struck through). */
export function RubricWithChanges({ task, emptyText = 'No rubric yet.' }: { task: Task; emptyText?: string }) {
  const d = reviewDiff(task)
  const removed = d?.rubric.filter((c) => c.kind === 'removed') ?? []
  const byId = new Map(d?.rubric.filter((c) => c.kind !== 'removed').map((c) => [c.after!.criterion_id, c]))
  if (!task.rubric.length && removed.length === 0) return <p className="empty">{emptyText}</p>
  return (
    <>
      <RubricView rubric={task.rubric} layout={task.rubric_layout} emptyText={emptyText} criterionClassName={c => {
        const change = byId.get(c.criterion_id)
        return change ? `is-${change.kind}` : ''
      }} renderCriterion={c => {
        const change = byId.get(c.criterion_id)
        return <>
          <span className="id">{c.criterion_id}</span>
          <div>
            <div className="crit">{c.criterion}{change && <span className={`chip ${change.kind === 'added' ? 'ok' : 'warn'}`} style={{ marginLeft: '.5rem' }}>{change.kind === 'added' ? 'added in review' : 'changed in review'}</span>}</div>
            {change?.kind === 'changed' && change.before && change.before.criterion !== c.criterion && <div className="detail was"><b>Was:</b> {change.before.criterion}</div>}
            <div className="tags"><span className={`chip ${c.role === 'required' ? 'neutral' : c.role === 'bonus' ? 'ok' : 'err'}`}>{c.role}</span></div>
            {c.evidence && <div className="detail"><b>Check:</b> {c.evidence}</div>}
          </div>
        </>
      }} />
      {!!removed.length && <ol className="rubric">
      {removed.map((r) => (
        <li key={`removed-${r.before!.criterion_id}`} className="is-removed">
          <span className="id">{r.before!.criterion_id}</span>
          <div><div className="crit"><del>{r.before!.criterion}</del> <span className="chip err">removed in review</span></div></div>
        </li>
      ))}
      </ol>}
    </>
  )
}
